/**
 * useRangeSelection
 *
 * Spreadsheet-like range selection for the DataTable.
 * - Click cell  -> single cell selected
 * - Click+drag  -> rectangular range
 * - Shift+click -> extend range from anchor
 * - Ctrl/Cmd+A  -> select all (table must be focused)
 * - Ctrl/Cmd+C  -> copy selection as TSV
 * - Esc         -> clear
 *
 * Disabled automatically on viewports <= 767px.
 *
 * @module components/common/DataTable/useRangeSelection
 */

import { useState, useEffect, useRef, useCallback, useMemo } from 'react';

const MOBILE_QUERY = '(max-width: 767px)';

const isInteractiveTarget = (el) => {
  if (!el) return false;
  if (el.closest('[data-no-select]')) return true;
  // Walk up a few levels checking for interactive controls
  let cur = el;
  let depth = 0;
  while (cur && depth < 6) {
    const tag = cur.tagName;
    if (tag === 'BUTTON' || tag === 'A' || tag === 'INPUT' || tag === 'SELECT' || tag === 'TEXTAREA') {
      return true;
    }
    if (cur.getAttribute && cur.getAttribute('data-no-select') !== null) return true;
    cur = cur.parentElement;
    depth += 1;
  }
  return false;
};

const buildTSV = (selection, getCellText) => {
  if (!selection) return '';
  const { rowStart, rowEnd, colStart, colEnd } = selection;
  const rMin = Math.min(rowStart, rowEnd);
  const rMax = Math.max(rowStart, rowEnd);
  const cMin = Math.min(colStart, colEnd);
  const cMax = Math.max(colStart, colEnd);
  const rows = [];
  for (let r = rMin; r <= rMax; r += 1) {
    const cols = [];
    for (let c = cMin; c <= cMax; c += 1) {
      const txt = getCellText ? getCellText(r, c) : '';
      cols.push(txt == null ? '' : String(txt));
    }
    rows.push(cols.join('\t'));
  }
  return rows.join('\n');
};

const writeClipboard = async (text) => {
  if (!text) return false;
  try {
    if (navigator?.clipboard?.writeText) {
      await navigator.clipboard.writeText(text);
      return true;
    }
  } catch {
    // fall through to execCommand
  }
  try {
    const ta = document.createElement('textarea');
    ta.value = text;
    ta.setAttribute('readonly', '');
    ta.style.position = 'absolute';
    ta.style.left = '-9999px';
    document.body.appendChild(ta);
    ta.select();
    const ok = document.execCommand('copy');
    document.body.removeChild(ta);
    return ok;
  } catch {
    return false;
  }
};

/**
 * @param {Object} options
 * @param {boolean} options.enabled - Master enable flag (caller controls)
 * @param {number} options.rowCount
 * @param {number} options.colCount
 * @param {(rowIdx:number, colIdx:number)=>string} options.getCellText
 * @param {React.RefObject<HTMLElement>} options.containerRef - Wrapper element ref (for focus check)
 */
const useRangeSelection = ({ enabled = true, rowCount = 0, colCount = 0, getCellText, containerRef }) => {
  const [isMobile, setIsMobile] = useState(() => {
    if (typeof window === 'undefined' || !window.matchMedia) return false;
    return window.matchMedia(MOBILE_QUERY).matches;
  });

  useEffect(() => {
    if (typeof window === 'undefined' || !window.matchMedia) return undefined;
    const mql = window.matchMedia(MOBILE_QUERY);
    const handler = (e) => setIsMobile(e.matches);
    if (mql.addEventListener) mql.addEventListener('change', handler);
    else if (mql.addListener) mql.addListener(handler);
    return () => {
      if (mql.removeEventListener) mql.removeEventListener('change', handler);
      else if (mql.removeListener) mql.removeListener(handler);
    };
  }, []);

  const active = enabled && !isMobile;

  // Selection: { rowStart, colStart, rowEnd, colEnd } | null
  const [selection, setSelection] = useState(null);
  // Anchor cell (origin of the selection)
  const anchorRef = useRef(null);
  const draggingRef = useRef(false);

  const clear = useCallback(() => {
    setSelection(null);
    anchorRef.current = null;
    draggingRef.current = false;
  }, []);

  const selectAll = useCallback(() => {
    if (rowCount <= 0 || colCount <= 0) return;
    const sel = { rowStart: 0, colStart: 0, rowEnd: rowCount - 1, colEnd: colCount - 1 };
    anchorRef.current = { row: 0, col: 0 };
    setSelection(sel);
  }, [rowCount, colCount]);

  const handleMouseDown = useCallback(
    (evt) => {
      if (!active) return;
      const target = evt.target;
      if (isInteractiveTarget(target)) return;
      const cell = target.closest('[data-row][data-col]');
      if (!cell) return;
      const row = Number(cell.getAttribute('data-row'));
      const col = Number(cell.getAttribute('data-col'));
      if (Number.isNaN(row) || Number.isNaN(col)) return;

      if (evt.shiftKey && anchorRef.current) {
        const a = anchorRef.current;
        setSelection({ rowStart: a.row, colStart: a.col, rowEnd: row, colEnd: col });
        return;
      }

      anchorRef.current = { row, col };
      draggingRef.current = true;
      setSelection({ rowStart: row, colStart: col, rowEnd: row, colEnd: col });
    },
    [active]
  );

  const handleMouseEnter = useCallback(
    (evt) => {
      if (!active || !draggingRef.current || !anchorRef.current) return;
      const cell = evt.target.closest('[data-row][data-col]');
      if (!cell) return;
      const row = Number(cell.getAttribute('data-row'));
      const col = Number(cell.getAttribute('data-col'));
      if (Number.isNaN(row) || Number.isNaN(col)) return;
      const a = anchorRef.current;
      setSelection({ rowStart: a.row, colStart: a.col, rowEnd: row, colEnd: col });
    },
    [active]
  );

  const handleMouseUp = useCallback(() => {
    draggingRef.current = false;
  }, []);

  // Global mouseup so drag releases off the table also stop dragging
  useEffect(() => {
    const up = () => {
      draggingRef.current = false;
    };
    window.addEventListener('mouseup', up);
    return () => window.removeEventListener('mouseup', up);
  }, []);

  // Keyboard: Esc / Ctrl+A / Ctrl+C — only when container has focus
  useEffect(() => {
    if (!active) return undefined;
    const handler = async (evt) => {
      const container = containerRef?.current;
      if (!container) return;
      const focused = container === document.activeElement || container.contains(document.activeElement);
      if (!focused) return;

      if (evt.key === 'Escape') {
        clear();
        return;
      }
      const isMod = evt.ctrlKey || evt.metaKey;
      if (isMod && (evt.key === 'a' || evt.key === 'A')) {
        evt.preventDefault();
        selectAll();
        return;
      }
      if (isMod && (evt.key === 'c' || evt.key === 'C')) {
        if (!selection) return;
        evt.preventDefault();
        const tsv = buildTSV(selection, getCellText);
        await writeClipboard(tsv);
      }
    };
    document.addEventListener('keydown', handler);
    return () => document.removeEventListener('keydown', handler);
  }, [active, selection, getCellText, containerRef, clear, selectAll]);

  const handlers = useMemo(
    () => ({
      onMouseDown: handleMouseDown,
      onMouseEnter: handleMouseEnter,
      onMouseUp: handleMouseUp,
    }),
    [handleMouseDown, handleMouseEnter, handleMouseUp]
  );

  // Helpers used by the table to style cells
  const isCellSelected = useCallback(
    (row, col) => {
      if (!selection) return false;
      const rMin = Math.min(selection.rowStart, selection.rowEnd);
      const rMax = Math.max(selection.rowStart, selection.rowEnd);
      const cMin = Math.min(selection.colStart, selection.colEnd);
      const cMax = Math.max(selection.colStart, selection.colEnd);
      return row >= rMin && row <= rMax && col >= cMin && col <= cMax;
    },
    [selection]
  );

  const isCellAnchor = useCallback(
    (row, col) => {
      const a = anchorRef.current;
      if (!a) return false;
      return a.row === row && a.col === col;
    },
    [selection] // eslint-disable-line react-hooks/exhaustive-deps
  );

  return {
    enabled: active,
    selection,
    anchor: anchorRef.current,
    handlers,
    selectAll,
    clear,
    isCellSelected,
    isCellAnchor,
    // exported for tests
    _buildTSV: (sel, getter) => buildTSV(sel, getter),
  };
};

export default useRangeSelection;
